import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FlexmonsterPivot } from 'ngx-flexmonster';
import Flexmonster, { Toolbar } from 'flexmonster';
import {
	STORM_CHART_MEASURES,
	STORM_SLICE_MEASURES,
	StormPivotRow,
	StormProperties,
	createPivotMapping,
	csvToJSON,
	getKeyByProp,
	getOrderedStormMeasures,
	getStormPivotOptions,
	getStormPivotSlice,
	normalizeStormDates,
} from './storms-pivot-table.constants';
import { environment } from 'app/environments/environment';
import { combineLatest, Subscription } from 'rxjs';
import { SliicerService } from 'app/shared/services/sliicer.service';
import { SliicerCaseStudy } from 'app/shared/models/sliicer';
import { DateutilService, TIME_12HOUR } from 'app/shared/services/dateutil.service';
import { DatePipe } from '@angular/common';
import { SnackBarNotificationService } from 'app/shared/services/snack-bar-notification.service';
import { TranslateService } from '@ngx-translate/core';
import { EXCLUDED_CHART_ITEMS, EXPORT_CSV_ID, EXPORT_EXCEL_ID, EXPORT_HTML_ID, EXPORT_IMG_ID, EXPORT_PDF_ID, EXPORT_VAULT_ID, STORM_EVENTS_EXPORT_NAME, TOOLBAR_CHARTS_TAB_ID, TOOLBAR_EXPORT_TAB_ID, TOOLBAR_FIELDS_TAB_ID, TOOLBAR_FORMAT_TAB_ID, TOOLBAR_FULLSCREEN_TAB_ID, TOOLBAR_GRID_TAB_ID, TOOLBAR_OPTIONS_TAB_ID, VAULT_ICON } from '../flexmonster.constant';
import { SNACK_BAR_NOTIFICATION_TIMEOUT } from 'app/shared/models/sliicer-data';
const blankText = '(blank)';

@Component({
	selector: 'app-storms-pivot-table',
	templateUrl: './storms-pivot-table.component.html',
	styleUrls: ['./storms-pivot-table.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class StormsPivotTableComponent implements OnInit, OnDestroy {
	public license = environment.flexmonsterLicense;
	public report: Flexmonster.Report;
	@ViewChild('pivot') private pivotTable: FlexmonsterPivot;

	public isLoading = true;

	private datePattern = '';
	private dateTimePattern = '';
	private caseStudyId: string;
	private customerId: number;

	private rawData: StormPivotRow[] = [];
	private data: StormPivotRow[] = [];
	public noData = false;

	private studyDateForExportNaming: string;

	private initialExportTabs: Flexmonster.ToolbarTab[];
	private subscriptions: Subscription[] = [];
	private isReportCompleteHandlerRegistered = false;
	private readonly reportCompleteHandler = (): void => {
		const report = this.pivotTable.flexmonster.getReport();

		if (typeof report === 'string') {
			return;
		}

		const slice = report.slice;
		const measures = slice?.measures;

		if (!measures) {
			return;
		}

		const orderedMeasures = getOrderedStormMeasures(measures);
		const isOrderChanged = orderedMeasures.some((measure, index) => measure !== measures[index]);

		if (!isOrderChanged) {
			return;
		}

		slice.measures = orderedMeasures;
		this.pivotTable.flexmonster.setReport(report);
	};

	constructor(
		private sliicerService: SliicerService,
		private dateutilService: DateutilService,
		private datePipe: DatePipe,
		private snackBarNotificationService: SnackBarNotificationService,
		private translate: TranslateService
	) { }

	ngOnInit(): void {
		this.subscriptions.push(
			combineLatest([this.dateutilService.dateFormat, this.dateutilService.timeFormat]).subscribe(
				([dateFormat, timeFormat]) => {
					this.datePattern = this.dateutilService.getStringFormat(dateFormat);
					this.dateTimePattern =
						timeFormat === TIME_12HOUR ? `${this.datePattern}, hh:mm tt` : `${this.datePattern}, HH:mm`;

					if (this.rawData.length) {
						this.setStormData(this.rawData);
					}
				},
			),
		);

		this.subscriptions.push(
			this.sliicerService.studyDetailsData$.subscribe((study: SliicerCaseStudy) => {
				this.caseStudyId = study.id;
				this.customerId = study.customerId;

				const format = this.dateutilService.getFormat().replace(/\//g, '');
				const start = new Date(study.config.startDate);
				const end = new Date(study.config.endDate);

				this.studyDateForExportNaming = `${this.datePipe.transform(start, format)}-${this.datePipe.transform(
					end,
					format,
				)}`;

				this.getStormsData();
			}),
		);
	}

	public customizeToolbar(toolbar: Toolbar) {
		toolbar.showShareReportTab = true;

		if (!this.isReportCompleteHandlerRegistered) {
			this.pivotTable.flexmonster.on('reportcomplete', this.reportCompleteHandler);
			this.isReportCompleteHandlerRegistered = true;
		}

		const tabs = this.getTabs(toolbar);

		toolbar.getTabs = function () {
			return tabs;
		}
	}

	private getTabs(toolbar: Toolbar, isGridView = false): Flexmonster.ToolbarTab[] {
		const tabs = toolbar.getTabs();

		const exportTab = tabs.find(v => v.id === TOOLBAR_EXPORT_TAB_ID);

		if (!this.initialExportTabs) {
			this.initialExportTabs = exportTab.menu;
		}

		const chartsTab = tabs.find(v => v.id === TOOLBAR_CHARTS_TAB_ID);
		const gridTab = tabs.find(v => v.id === TOOLBAR_GRID_TAB_ID);

		const formatTab = tabs.find(v => v.id === TOOLBAR_FORMAT_TAB_ID);
		const optionsTab = tabs.find(v => v.id === TOOLBAR_OPTIONS_TAB_ID);
		const fieldsTab = tabs.find(v => v.id === TOOLBAR_FIELDS_TAB_ID);
		const fullScreenTab = tabs.find(v => v.id === TOOLBAR_FULLSCREEN_TAB_ID);

		this.formatExportsTab(exportTab, isGridView);

		gridTab.handler = this.onGridView.bind(this);

		chartsTab.menu = chartsTab.menu.filter(v => !EXCLUDED_CHART_ITEMS.includes(v.id));
		chartsTab.menu.forEach((item) => {
			item.handler = this.onChartView.bind(this);
		});


		return [
			exportTab,
			{ divider: true } as Flexmonster.ToolbarTab,
			chartsTab,
			gridTab,
			formatTab,
			optionsTab,
			fieldsTab,
			{ divider: true } as Flexmonster.ToolbarTab,
			fullScreenTab
		];
	}

	private getStormsData(): void {
		this.isLoading = true;
		this.rawData = [];
		this.data = [];

		this.sliicerService.getAllStormResults(this.customerId, this.caseStudyId).subscribe(
			(csv) => {
				if (!csv) {
					this.isLoading = false;
					this.noData = true;
					return;
				}

				const json = csvToJSON(csv);
				if (!json.length) {
					this.isLoading = false;
					this.noData = true;
					return;
				}

				// need to remove extra ""
				const data = json.map((item): StormPivotRow => {
					const row: StormPivotRow = {};

					Object.keys(item).forEach((itemKey) => {
						const key = itemKey.replace(/"/g, '');
						row[key] = item[itemKey];
					});

					return row;
				});

				this.rawData = data.slice(0, data.length - 1);
				this.setStormData(this.rawData);
			},
			() => {
				this.isLoading = false;
				this.noData = true;
			},
		);
	}

	private setStormData(data: ReadonlyArray<StormPivotRow>): void {
		this.data = normalizeStormDates(data, this.dateutilService.dateFormat.getValue());

		if (!this.data.length) {
			this.isLoading = false;
			this.noData = true;
			return;
		}

		this.noData = false;
		this.generateTable(this.data);
	}

	private onChartView(type: string) {
		this.pivotTable.flexmonster.showCharts(type, true);

		const report = this.pivotTable.flexmonster.getReport() as Flexmonster.Report;
		(report.slice as Flexmonster.Slice).measures = STORM_CHART_MEASURES.map(prop => ({ uniqueName: getKeyByProp(this.data[0], prop) }));
		this.pivotTable.flexmonster.setReport(report);

		const tabs = this.getTabs(this.pivotTable.flexmonster.toolbar, false);
		this.pivotTable.flexmonster.toolbar.getTabs = function () { return tabs; }
		this.pivotTable.flexmonster.toolbar['create']();
	}

	private onGridView() {
		this.pivotTable.flexmonster.showGrid();

		const report = this.pivotTable.flexmonster.getReport() as Flexmonster.Report;
		(report.slice as Flexmonster.Slice).measures = STORM_SLICE_MEASURES.map(prop => ({ uniqueName: getKeyByProp(this.data[0], prop) }));
		this.pivotTable.flexmonster.setReport(report);

		const tabs = this.getTabs(this.pivotTable.flexmonster.toolbar, true);
		this.pivotTable.flexmonster.toolbar.getTabs = function () { return tabs; }
		this.pivotTable.flexmonster.toolbar['create']();
	}

	private formatExportsTab(tab: Flexmonster.ToolbarTab, isGridView: boolean) {
		let exportTypes: string[] = [];

		if (isGridView) {
			exportTypes = [EXPORT_HTML_ID, EXPORT_CSV_ID, EXPORT_EXCEL_ID];
		} else {
			exportTypes = [EXPORT_HTML_ID, EXPORT_CSV_ID, EXPORT_EXCEL_ID, EXPORT_IMG_ID, EXPORT_PDF_ID];
		}

		tab.menu = this.initialExportTabs.filter(menuItem => {
			if (!exportTypes.includes(menuItem.id)) {
				return false;
			}

			this.setExportHandler(menuItem);

			return true;
		});

		if (isGridView) {
			tab.menu.unshift({
				title: 'To Vault(Raw)',
				id: EXPORT_VAULT_ID,
				icon: VAULT_ICON,
				handler: () => this.exportVaultHandler()
			});
		}
	}

	private setExportHandler(menuItem: Flexmonster.ToolbarTab) {
		switch (menuItem.id) {
			case EXPORT_HTML_ID: {
				menuItem.handler = this.exportToHTMLhandler();
				break;
			}
			case EXPORT_EXCEL_ID: {
				menuItem.handler = this.exportToExcelHandler()
				break;
			}
			case EXPORT_CSV_ID: {
				menuItem.handler = this.exportToCsvHandler();
				break;
			}
			case EXPORT_IMG_ID: {
				menuItem.handler = this.exportToImageHandler();
				break;
			}
			case EXPORT_PDF_ID: {
				menuItem.handler = this.exportToPdfHandler();
				break;
			}
			default: break;
		}
	}

	private removeBlanks() {
		this.pivotTable.flexmonster.customizeCell((cell, data) => {
		  if (data.label === blankText) {
			cell.text = '';
		  }
		});
	}

	private exportToExcelHandler() {
		this.removeBlanks();
		return () => this.pivotTable.flexmonster.exportTo('excel', {
			filename: `${STORM_EVENTS_EXPORT_NAME}-${this.studyDateForExportNaming}`,
			excelSheetName: `${STORM_EVENTS_EXPORT_NAME}-${this.studyDateForExportNaming}`,
		});

	}

	private exportToCsvHandler() {
		this.removeBlanks();
		return () => this.pivotTable.flexmonster.exportTo('csv', {
			filename: `${STORM_EVENTS_EXPORT_NAME}-${this.studyDateForExportNaming}`,
		});
	}

	private exportToHTMLhandler() {
		return () => this.pivotTable.flexmonster.exportTo('html', {
			filename: `${STORM_EVENTS_EXPORT_NAME}-${this.studyDateForExportNaming}`,
		});
	}

	private exportToImageHandler() {
		return () => this.pivotTable.flexmonster.exportTo('image', {
			filename: `${STORM_EVENTS_EXPORT_NAME}-${this.studyDateForExportNaming}`,
		});
	}

	private exportToPdfHandler() {
		return () => this.pivotTable.flexmonster.exportTo('pdf', {
			filename: `${STORM_EVENTS_EXPORT_NAME}-${this.studyDateForExportNaming}`,
		});
	}

	private exportVaultHandler() {
		const successMessage = this.translate.instant('SLIICER_TABLE.SLICER_SUMMARY.RESULTS.EXPORT_STORMS_SAVE_MESSAGE');
		const dismissText = this.translate.instant('COMMON.DISMISS_TEXT');
		const errorText = this.translate.instant('SLIICER_TABLE.SLICER_SUMMARY.RESULTS.EXPORT_STORMS_ERROR_MESSAGE');

		this.isLoading = true;
		this.sliicerService.exportStorms(this.customerId, this.caseStudyId).subscribe(
			() => {
				this.isLoading = false;
				this.snackBarNotificationService.raiseNotification(
					successMessage,
					dismissText,
					{
						duration: SNACK_BAR_NOTIFICATION_TIMEOUT,
					},
					true,
				);
			},
			() => {
				this.isLoading = false;
				this.snackBarNotificationService.raiseNotification(
					errorText,
					dismissText,
					{
						panelClass: 'custom-error-snack-bar',
					},
					false,
				);
			},
		);

	}

	private generateTable(data: StormPivotRow[]): void {
		const mapping = createPivotMapping(data[0], this.dateTimePattern);

		this.report = {
			options: getStormPivotOptions(
				this.datePattern,
				this.dateTimePattern,
				getKeyByProp(data[0], StormProperties.eventNet2Vol),
			),
			dataSource: { data, mapping },
			slice: getStormPivotSlice(data[0])
		};

		const format = {
			name: "",
			thousandsSeparator: "",
		}

		this.isLoading = false;
		this.pivotTable.flexmonster.setReport(this.report);
		this.pivotTable.flexmonster.setFormat(format);
		this.pivotTable.flexmonster.refresh();
	}

	ngOnDestroy() {
		if (this.isReportCompleteHandlerRegistered) {
			this.pivotTable?.flexmonster?.off('reportcomplete', this.reportCompleteHandler);
			this.isReportCompleteHandlerRegistered = false;
		}

		this.subscriptions.forEach(v => v.unsubscribe());
	}
}
